import { FC, ReactNode } from "react"
import { cn } from "../../utils"
import BBSwitch from "../form/Switch"

function ConfigAdminFieldKeySuffix({ fieldKey }: { fieldKey: string }) {
  return (
    <span className="text-xs font-normal text-gray-400">
      {" · "}
      <span className="font-mono">{fieldKey}</span>
    </span>
  )
}

interface ConfigAdminFieldLabelHintProps {
  label: string
  hint?: string
  htmlFor?: string
  /** Config / API key shown after the label (smaller, gray). */
  fieldKey?: string
}

export const ConfigAdminFieldLabelHint: FC<ConfigAdminFieldLabelHintProps> = ({
  label,
  hint,
  htmlFor,
  fieldKey
}) => (
  <>
    <label htmlFor={htmlFor} className="mb-0.5 block">
      <span className="text-sm font-medium text-gray-600">{label}</span>
      {fieldKey ? <ConfigAdminFieldKeySuffix fieldKey={fieldKey} /> : null}
    </label>
    {hint ? <p className="text-xs text-gray-400 mb-1.5">{hint}</p> : null}
  </>
)

interface ConfigAdminPanelStackedFieldProps {
  label: string
  hint?: string
  htmlFor?: string
  fieldKey?: string
  dividerBottom?: boolean
  children: ReactNode
}

export const ConfigAdminPanelStackedField: FC<
  ConfigAdminPanelStackedFieldProps
> = ({ label, hint, htmlFor, fieldKey, dividerBottom = true, children }) => (
  <div className={cn("px-4 py-3", dividerBottom && "border-b border-gray-100")}>
    <ConfigAdminFieldLabelHint
      label={label}
      hint={hint}
      htmlFor={htmlFor}
      fieldKey={fieldKey}
    />
    {children}
  </div>
)

interface ConfigAdminToggleRowProps {
  label: string
  /** Config key shown after the label (smaller, gray). Omit when label is already the raw key. */
  fieldKey?: string
  description?: string
  enabled: boolean
  onChange: (val: boolean) => void
  onDelete?: () => void
  last?: boolean
}

export const ConfigAdminToggleRow: FC<ConfigAdminToggleRowProps> = ({
  label,
  fieldKey,
  description,
  enabled,
  onChange,
  onDelete,
  last
}) => (
  <div
    className={cn(
      "flex items-center justify-between px-4 py-3 transition-colors hover:bg-gray-50",
      !last && "border-b border-gray-100"
    )}
  >
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-gray-700">
        <span>{label}</span>
        {fieldKey ? <ConfigAdminFieldKeySuffix fieldKey={fieldKey} /> : null}
      </p>
      {description ? (
        <p className="text-xs text-gray-400">{description}</p>
      ) : null}
    </div>
    <div className="ml-4 flex shrink-0 items-center gap-3">
      <BBSwitch enabled={enabled} setEnabled={onChange} />
      {onDelete ? (
        <button
          type="button"
          onClick={onDelete}
          className="text-lg leading-none text-gray-300 transition-colors hover:text-red-400"
          aria-label={`Remove ${label}`}
        >
          ×
        </button>
      ) : null}
    </div>
  </div>
)
